import { cn, NoddiIcon, NoddiIconName } from 'noddi-ui-common';
import { type Currency } from 'noddi-util';
import { ReactNode } from 'react';
import { NoddiCopyButton, NoddiCopyButtonVariant } from '../Inputs/NoddiCopyButton';
import { NoddiIconButton } from '../Inputs/NoddiIconButton';
import { NoddiTooltip } from '../Overlay/NoddiTooltip';
import { formatInfoValue, getInfoTextToCopy, InfoValueType } from './infoFieldValue';
import { NoddiLink } from './NoddiLink';

interface NoddiInfoFieldProps {
  label: string;
  tooltip?: string;
  value?: ReactNode;
  type?: InfoValueType;
  currency?: Currency;
  className?: string;
  copyToClipboard?: boolean;
  copyButtonVariant?: NoddiCopyButtonVariant;
  textToCopy?: string;
  icon?: NoddiIconName;
  linkTo?: string;
  trailing?: ReactNode;
  variant?: 'row' | 'column';
  labelClassName?: string;
  valueClassName?: string;
  target?: React.HTMLAttributeAnchorTarget;
  onEdit?: () => void;
}

export const NoddiInfoField = ({
  label,
  value,
  type,
  currency,
  className = '',
  tooltip,
  copyToClipboard = false,
  copyButtonVariant = 'default',
  textToCopy,
  icon,
  linkTo,
  trailing,
  variant = 'row',
  labelClassName,
  valueClassName,
  target,
  onEdit
}: NoddiInfoFieldProps) => {
  const formattedValue = formatInfoValue(value, type, currency);
  const isColumn = variant === 'column';
  const resolvedCopyText = textToCopy ?? getInfoTextToCopy(value, formattedValue, type);

  return (
    <div
      className={cn('flex gap-4', isColumn ? 'flex-col items-start gap-1' : 'items-start justify-between', className)}
    >
      {/* Extra wrapper div to group label and tooltip, vertical alignment handled by child flex container. */}
      <div>
        <div className={cn('flex items-center gap-2', isColumn && 'mb-0')}>
          {icon && <NoddiIcon className={cn('text-gray-600')} name={icon} size='small' />}
          <span className={cn('whitespace-nowrap text-gray-600', isColumn && 'text-sm font-semibold', labelClassName)}>
            {label}
          </span>
          {tooltip && <NoddiTooltip content={tooltip} />}
          {onEdit && (
            <NoddiIconButton
              aria-label={`Edit ${label}`}
              iconName='Edit'
              size='small'
              variant='subtle'
              onClick={onEdit}
            />
          )}
        </div>
      </div>
      <div className={cn('flex items-center gap-2', isColumn && 'w-full')}>
        {linkTo ? (
          /* need extra div to make link variant respect the gap between label and value */
          <div>
            <NoddiLink
              variant='link'
              target={target}
              to={linkTo}
              className={cn('h-fit py-0 pr-0', isColumn && 'pl-0')}
              spanClassName={cn('[overflow-wrap:anywhere] whitespace-normal', !isColumn && 'text-right')}
            >
              {formattedValue}
            </NoddiLink>
          </div>
        ) : (
          <span className={cn('[overflow-wrap:anywhere]', !isColumn && 'text-right', valueClassName)}>
            {formattedValue}
          </span>
        )}
        {copyToClipboard && resolvedCopyText && resolvedCopyText !== '-' && (
          <NoddiCopyButton
            textToCopy={resolvedCopyText}
            variant={copyButtonVariant}
            successMessage={`${label} copied to clipboard!`}
          />
        )}
        {trailing}
      </div>
    </div>
  );
};
